import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { isRetryable } from '../operator/operatorErrors';
import { earlierVisitFailure, offScreenFailure, offScreenSignal } from '../operator/operatorTaskScope';
import { pickLine, reservePickingOrder, scanPickingCode, shortPickLine, unpickLine } from './operatorPickingThunks';
import { pickMessage, reserveMessage, shortPickMessage, unpickMessage } from './operatorPickingUtils';

/**
 * A write's band. Its answer can come once its order is off screen
 * (`meta.offScreenTask`, see the thunks): the band then names the order and
 * says no step to take. A lost answer offers «Reintentar» wherever it shows:
 * the band carries the write, and its key keeps it from registering twice.
 */
const write = (message, failed) => ({
  success: ({ payload, meta }) => {
    const name = meta.offScreenTask;
    const signal = message(payload, meta.arg, { onScreen: !name });
    return name ? offScreenSignal(name, signal) : signal;
  },
  error: ({ payload, meta }, fallback) => {
    const name = meta.offScreenTask;
    if (meta.earlierVisit) return earlierVisitFailure(payload);
    if (!name) return undefined;
    return isRetryable(payload) ? offScreenSignal(name, fallback) : offScreenFailure(name, payload, failed);
  },
  retry: OPERATOR_FLOW.PICKING,
  keyed: true,
});

/** What the band says for each picking outcome; see `operatorListeners`. */
export const PICKING_SIGNAL_RULES = {
  [scanPickingCode.typePrefix]: {
    success: ({ payload }) => {
      if (payload.kind === 'slot') {
        const where = payload.target.slot_name || payload.target.slot_code;
        return payload.suggested
          ? { tone: 'step', message: `En ${where}. Escanea el producto.` }
          : { tone: 'warning', message: `${where} no es una sugerida: si no tiene stock, el pick lo rechaza.` };
      }
      return {
        tone: 'step',
        message: payload.product.factor > 1 ? `Caja ×${payload.product.factor}. ¿Cuántas?` : '¿Cuántos sacas?',
      };
    },
  },
  [pickLine.typePrefix]: write(pickMessage),
  [shortPickLine.typePrefix]: write(shortPickMessage),
  [unpickLine.typePrefix]: write(unpickMessage),
  [reservePickingOrder.typePrefix]: {
    ...write(reserveMessage, 'no se reservó'),
    // Its message names the order already.
    success: ({ payload, meta }) => reserveMessage(payload, meta.arg, { onScreen: !meta.offScreenTask }),
  },
};
